// ─── src/NpdRegistrationsView.tsx ────────────────────────────────────────────
//
// Full founder registrations for National Pitching Day. The lightweight
// NpdWaitlist view only tracks name/email/startup/governorate for the
// next cycle; this one shows the full application (two-section form)
// used by the current cohort's selection committee.

import { useEffect, useState } from "react";
import {
  Download,
  Eye,
  MapPin,
  Plus,
  Rocket,
  Save,
  Search,
  Trash2,
  X,
} from "lucide-react";
import {
  useDeleteNpdRegistration,
  useNpdRegistrationStats,
  useNpdRegistrations,
  useReviewNpdRegistration,
} from "./hooks/useNpd";
import { Pagination } from "./components/dashboard/Pagination";
import { TableSkeleton } from "./components/ui/Skeleton";
import { StatCard } from "./components/ui/StatCard";
import type { NpdAdminStatus, NpdRegistration } from "./api/npd";

const LIMIT = 20;

const ADMIN_STATUS_LABEL: Record<NpdAdminStatus, string> = {
  new_applicant: "New applicant",
  shortlisted: "Shortlisted",
  final: "Final",
};

const ADMIN_STATUS_ORDER: NpdAdminStatus[] = [
  "new_applicant",
  "shortlisted",
  "final",
];

const ADMIN_STATUS_CLASS: Record<NpdAdminStatus, string> = {
  new_applicant: "bg-slate-500/15 text-slate-200 border border-slate-500/40",
  shortlisted: "bg-amber-500/15 text-amber-300 border border-amber-500/40",
  final: "bg-brand-green/15 text-brand-green border border-brand-green/40",
};

function AdminStatusPill({ status }: { status: NpdAdminStatus }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${ADMIN_STATUS_CLASS[status]}`}
    >
      {ADMIN_STATUS_LABEL[status]}
    </span>
  );
}

// ── Label dictionaries ──────────────────────────────────────────────────────
// Keys match the backend DTO enums; English labels are the admin-facing
// strings. Keep in sync with mainwebsite/src/components/npd/NpdRegistrationForm.
const GENDER_LABEL: Record<string, string> = {
  male: "Male",
  female: "Female",
  prefer_not_to_say: "Prefer not to say",
};

const AGE_LABEL: Record<string, string> = {
  under_18: "Under 18",
  "18_24": "18–24",
  "24_35": "24–35",
  "35_60": "35–60",
  over_60: "60+",
};

const GOVERNORATE_LABEL: Record<string, string> = {
  damascus: "Damascus",
  rif_dimashq: "Rif Dimashq",
  aleppo: "Aleppo",
  homs: "Homs",
  hama: "Hama",
  latakia: "Latakia",
  tartus: "Tartus",
  idlib: "Idlib",
  daraa: "Daraa",
  sweida: "Sweida",
  deir_ezzor: "Deir ez-Zor",
  hasakah: "Hasakah",
  raqqa: "Raqqa",
  quneitra: "Quneitra",
};

const STATUS_LABEL: Record<string, string> = {
  student: "Student",
  employee: "Employee",
  freelancer: "Freelancer",
  founder: "Founder",
  graduate: "Graduate",
};

const SKILL_LABEL: Record<string, string> = {
  entrepreneurship: "Entrepreneurship",
  business: "Business",
  expert: "Expert",
  developer: "Developer",
  designer: "Designer",
};

/**
 * The umbrellas the form offers today. Each row maps slug → the
 * admin-facing English label (the Arabic version is on the public
 * form).
 */
const UMBRELLA_LABEL: Record<string, string> = {
  technology_digital: "Technology & Digital",
  health_education: "Health & Education",
  industry_infrastructure_logistics: "Industry, Infrastructure & Logistics",
  food_agri_environment_energy: "Food, Agri, Environment & Energy",
  economy_social_impact: "Economy & Social Impact",
  media_creative_consumer: "Media, Creative & Consumer",
};

/**
 * Legacy sector leaves from earlier form versions. Kept so historic
 * rows render as the leaf's own label; the mapping to the current
 * umbrella is in SECTOR_UMBRELLA below.
 */
const LEGACY_LEAF_LABEL: Record<string, string> = {
  education_skills: "Education & Skills Development",
  infrastructure_public_services: "Infrastructure & Public Services",
  healthcare_medical_access: "Healthcare & Medical Access",
  economic_development_employment: "Economic Development & Employment",
  food_security_agriculture: "Food Security & Agriculture",
  peace_social_cohesion: "Peace & Social Cohesion",
  fintech_digital_payments: "Fintech & Digital Payments",
  software_saas: "Software & SaaS",
  digital_services: "Digital Services",
  ai_data_analytics: "AI, Data & Analytics",
  robotics_iot: "Robotics & IoT",
  hardware: "Hardware",
  cybersecurity: "Cybersecurity",
  healthtech_biotech: "HealthTech & Biotech",
  mental_health_wellness: "Mental Health & Wellness",
  hrtech_jobs: "HR-tech & Jobs",
  logistics_delivery: "Logistics & Delivery",
  mobility_transport: "Mobility & Transport",
  manufacturing_construction: "Manufacturing & Construction",
  agritech_water: "AgriTech & Water",
  clean_energy_climate: "Clean Energy & Climate",
  social_enterprises_ngos: "Social Enterprises & NGOs",
  media_creative: "Media & Creative Industries",
  tourism_hospitality: "Tourism & Hospitality",
  consumer_products: "Consumer Products",
  retail_ecommerce: "Retail & E-commerce",
};

const SECTOR_LABEL: Record<string, string> = {
  ...UMBRELLA_LABEL,
  ...LEGACY_LEAF_LABEL,
};

/**
 * Normalize a raw sector value to its umbrella name. A value that's
 * already an umbrella (new submissions) returns its own label; a
 * legacy leaf (older rows) is looked up in LEAF_TO_UMBRELLA.
 */
const LEAF_TO_UMBRELLA: Record<string, string> = {
  software_saas: "technology_digital",
  digital_services: "technology_digital",
  ai_data_analytics: "technology_digital",
  robotics_iot: "technology_digital",
  hardware: "technology_digital",
  cybersecurity: "technology_digital",
  fintech_digital_payments: "technology_digital",
  healthcare_medical_access: "health_education",
  healthtech_biotech: "health_education",
  mental_health_wellness: "health_education",
  education_skills: "health_education",
  hrtech_jobs: "health_education",
  infrastructure_public_services: "industry_infrastructure_logistics",
  logistics_delivery: "industry_infrastructure_logistics",
  mobility_transport: "industry_infrastructure_logistics",
  manufacturing_construction: "industry_infrastructure_logistics",
  food_security_agriculture: "food_agri_environment_energy",
  agritech_water: "food_agri_environment_energy",
  clean_energy_climate: "food_agri_environment_energy",
  economic_development_employment: "economy_social_impact",
  peace_social_cohesion: "economy_social_impact",
  social_enterprises_ngos: "economy_social_impact",
  media_creative: "media_creative_consumer",
  tourism_hospitality: "media_creative_consumer",
  consumer_products: "media_creative_consumer",
  retail_ecommerce: "media_creative_consumer",
};

function umbrellaOf(sector: string): string {
  if (UMBRELLA_LABEL[sector]) return UMBRELLA_LABEL[sector];
  const umbrella = LEAF_TO_UMBRELLA[sector];
  return umbrella ? (UMBRELLA_LABEL[umbrella] ?? umbrella) : "Other";
}

const STAGE_LABEL: Record<string, string> = {
  idea: "Idea",
  mvp: "MVP",
  early_revenue: "Early Revenue",
  scaling: "Scaling",
};

const REVENUE_RANGE_LABEL: Record<string, string> = {
  "1_1000": "$1 – $1,000",
  "1001_5000": "$1,001 – $5,000",
  "5001_10000": "$5,001 – $10,000",
  over_10000: "Over $10,000",
};

const CSV_HEADERS = [
  "Primary contact",
  "Email",
  "WhatsApp",
  "Gender",
  "Age",
  "Governorate",
  "City",
  "Current status",
  "Skills",
  "LinkedIn",
  "Co-founders",
  "Startup",
  "One-liner",
  "Startup city",
  "Sector",
  "Established",
  "Stage",
  "Problem",
  "Has users / revenue",
  "Annual revenue",
  "Customer count",
  "Submitted at",
];

function exportCsv(rows: NpdRegistration[]) {
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const body = rows.map((r) =>
    [
      r.fullName,
      r.email,
      r.whatsapp,
      GENDER_LABEL[r.gender] ?? r.gender,
      AGE_LABEL[r.ageBracket] ?? r.ageBracket,
      GOVERNORATE_LABEL[r.governorate] ?? r.governorate,
      r.city,
      STATUS_LABEL[r.currentStatus] ?? r.currentStatus,
      (r.skills ?? []).map((s) => SKILL_LABEL[s] ?? s).join(" · "),
      r.linkedinUrl ?? "",
      (r.founders ?? [])
        .map((f) =>
          f.linkedinUrl ? `${f.name} <${f.linkedinUrl}>` : f.name,
        )
        .join(" · "),
      r.startupName,
      r.startupDescription,
      r.startupCity,
      umbrellaOf(r.sector),
      r.establishYear != null ? String(r.establishYear) : "",
      STAGE_LABEL[r.stage] ?? r.stage,
      r.problemSolved,
      r.hasUsersOrRevenue ? "Yes" : "No",
      r.annualRevenueRange
        ? (REVENUE_RANGE_LABEL[r.annualRevenueRange] ?? r.annualRevenueRange)
        : "",
      r.customerCount != null ? String(r.customerCount) : "",
      r.createdAt,
    ]
      .map(esc)
      .join(","),
  );
  const blob = new Blob(["﻿" + [CSV_HEADERS.join(","), ...body].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `npd_registrations_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function NpdRegistrationsView() {
  const [search, setSearch] = useState("");
  const [governorate, setGovernorate] = useState("");
  const [sector, setSector] = useState("");
  const [stage, setStage] = useState("");
  const [adminStatus, setAdminStatus] = useState<NpdAdminStatus | "">("");
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<NpdRegistration | null>(null);

  const { data: stats } = useNpdRegistrationStats();
  const { data: allData } = useNpdRegistrations({ page: 1, limit: 2000 });
  const {
    data: tableData,
    isLoading,
    isFetching,
    isError,
  } = useNpdRegistrations({
    page,
    limit: LIMIT,
    search,
    governorate,
    sector,
    stage,
    adminStatus: adminStatus || undefined,
  });

  // Keep the open drawer in sync with the latest row (so the status
  // chip + tags refresh after a review patch invalidates the query).
  useEffect(() => {
    if (!detail) return;
    const latest = tableData?.data.find((r) => r.id === detail.id);
    if (latest && latest.updatedAt !== detail.updatedAt) {
      setDetail(latest);
    }
  }, [tableData, detail]);

  const { mutate: deleteRow, isPending: isDeleting } =
    useDeleteNpdRegistration();

  const rows = tableData?.data ?? [];
  const meta =
    tableData?.meta ?? { total: 0, page: 1, limit: LIMIT, totalPages: 1 };

  const topGov = Object.entries(stats?.byGovernorate ?? {})
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);

  const govOptions = Object.keys(stats?.byGovernorate ?? {}).sort();
  const stageOptions = Object.keys(stats?.byStage ?? {}).sort();

  // The form only offers umbrellas today, so filter choices match.
  // Legacy leaves still show in the table (they're normalized to an
  // umbrella in the Sector column and the CSV via umbrellaOf()).
  const umbrellaFilterOptions = Object.keys(UMBRELLA_LABEL);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label="Registrations"
          value={stats?.total ?? 0}
          icon={Rocket}
          accent="bg-brand-orange"
        />
        <StatCard
          label="New applicants"
          value={stats?.byAdminStatus?.new_applicant ?? 0}
          icon={Rocket}
          accent="bg-slate-500"
        />
        <StatCard
          label="Shortlisted"
          value={stats?.byAdminStatus?.shortlisted ?? 0}
          icon={Rocket}
          accent="bg-amber-500"
        />
        <StatCard
          label="Final"
          value={stats?.byAdminStatus?.final ?? 0}
          icon={Rocket}
          accent="bg-brand-green"
        />
      </div>

      {/* Governorate strip — smaller, under the status row */}
      {topGov.length > 0 && (
        <div className="flex flex-wrap gap-2 text-xs text-brand-muted">
          <span className="font-mono uppercase tracking-widest">
            Top governorates:
          </span>
          {topGov.map(([g, count]) => (
            <span
              key={g}
              className="inline-flex items-center gap-1 rounded-full border border-brand-border bg-brand-navy px-2 py-0.5"
            >
              <MapPin size={10} />
              {GOVERNORATE_LABEL[g] ?? g}: {count}
            </span>
          ))}
        </div>
      )}

      <div className="card p-4 flex flex-col lg:flex-row items-start lg:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search founder, startup, email…"
            className="w-full pl-9 pr-8 py-2 text-sm bg-brand-navy border border-brand-border rounded-lg text-white placeholder-brand-muted focus:outline-none focus:border-brand-green"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted hover:text-white"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <select
          value={governorate}
          onChange={(e) => {
            setGovernorate(e.target.value);
            setPage(1);
          }}
          className="py-2 px-3 text-sm bg-brand-navy border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green"
        >
          <option value="">All governorates</option>
          {govOptions.map((g) => (
            <option key={g} value={g}>
              {GOVERNORATE_LABEL[g] ?? g}
            </option>
          ))}
        </select>
        <select
          value={sector}
          onChange={(e) => {
            setSector(e.target.value);
            setPage(1);
          }}
          className="py-2 px-3 text-sm bg-brand-navy border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green"
        >
          <option value="">All sectors</option>
          {umbrellaFilterOptions.map((u) => (
            <option key={u} value={u}>
              {UMBRELLA_LABEL[u]}
            </option>
          ))}
        </select>
        <select
          value={stage}
          onChange={(e) => {
            setStage(e.target.value);
            setPage(1);
          }}
          className="py-2 px-3 text-sm bg-brand-navy border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green"
        >
          <option value="">All stages</option>
          {stageOptions.map((s) => (
            <option key={s} value={s}>
              {STAGE_LABEL[s] ?? s}
            </option>
          ))}
        </select>
        <select
          value={adminStatus}
          onChange={(e) => {
            setAdminStatus(e.target.value as NpdAdminStatus | "");
            setPage(1);
          }}
          className="py-2 px-3 text-sm bg-brand-navy border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green"
        >
          <option value="">All pipeline statuses</option>
          {ADMIN_STATUS_ORDER.map((s) => (
            <option key={s} value={s}>
              {ADMIN_STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <button
          onClick={() => exportCsv(allData?.data ?? [])}
          disabled={(allData?.data ?? []).length === 0}
          className="btn-primary text-sm flex items-center gap-1.5 ml-auto disabled:opacity-50"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <div className="card p-8 text-center text-red-400">
          Failed to load registrations.
        </div>
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center text-brand-muted">
          No registrations yet.
        </div>
      ) : (
        <>
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-brand-border text-left text-brand-muted">
                  <th className="px-4 py-3 font-medium">#</th>
                  <th className="px-4 py-3 font-medium">Founder</th>
                  <th className="px-4 py-3 font-medium">Startup</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Sector</th>
                  <th className="px-4 py-3 font-medium">Stage</th>
                  <th className="px-4 py-3 font-medium">Governorate</th>
                  <th className="px-4 py-3 font-medium">Submitted</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.id}
                    className="border-b border-brand-border/50 hover:bg-brand-border/20 transition-colors"
                  >
                    <td className="px-4 py-3 text-brand-muted">
                      {(meta.page - 1) * LIMIT + i + 1}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">
                        {r.fullName}
                      </div>
                      <div className="text-brand-muted text-xs">{r.email}</div>
                    </td>
                    <td className="px-4 py-3 text-white">
                      <div>{r.startupName}</div>
                      {r.adminTags && r.adminTags.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {r.adminTags.slice(0, 3).map((t) => (
                            <span
                              key={t}
                              className="inline-block rounded-full border border-brand-border bg-brand-navy px-1.5 py-0 text-[9px] text-brand-muted"
                            >
                              {t}
                            </span>
                          ))}
                          {r.adminTags.length > 3 && (
                            <span className="text-[9px] text-brand-muted">
                              +{r.adminTags.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <AdminStatusPill status={r.adminStatus} />
                    </td>
                    <td className="px-4 py-3 text-brand-muted">
                      {umbrellaOf(r.sector)}
                    </td>
                    <td className="px-4 py-3 text-brand-muted">
                      {STAGE_LABEL[r.stage] ?? r.stage}
                    </td>
                    <td className="px-4 py-3 text-brand-muted">
                      {GOVERNORATE_LABEL[r.governorate] ?? r.governorate}
                    </td>
                    <td className="px-4 py-3 text-brand-muted whitespace-nowrap">
                      {new Date(r.createdAt).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>
                    <td className="px-4 py-3 flex items-center gap-1">
                      <button
                        onClick={() => setDetail(r)}
                        className="p-1.5 rounded-lg text-brand-muted hover:text-brand-green hover:bg-brand-green/10"
                        title="View details"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm("Delete this registration?"))
                            deleteRow(r.id);
                        }}
                        disabled={isDeleting}
                        className="p-1.5 rounded-lg text-brand-muted hover:text-red-400 hover:bg-red-500/10 disabled:opacity-40"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={meta.page}
            totalPages={meta.totalPages}
            total={meta.total}
            limit={LIMIT}
            onPageChange={setPage}
            isFetching={isFetching}
          />
        </>
      )}

      {detail && (
        <RegistrationDetailDrawer
          row={detail}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}

/**
 * Full-record drawer. Shows every answered field in the two-section
 * form, with the enum values decoded via the label dictionaries above.
 */
function RegistrationDetailDrawer({
  row,
  onClose,
}: {
  row: NpdRegistration;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/50"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl h-full overflow-y-auto bg-brand-navy border-l border-brand-border p-6 space-y-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-brand-muted">
              Registration
            </p>
            <h2 className="mt-1 text-xl font-bold text-white">
              {row.startupName}
            </h2>
            <p className="text-sm text-brand-muted">by {row.fullName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border/40"
          >
            <X size={16} />
          </button>
        </div>

        <ReviewEditor row={row} />

        <section className="space-y-3">
          <h3 className="text-xs font-mono uppercase tracking-widest text-brand-orange">
            Personal
          </h3>
          <DetailRow label="Email" value={row.email} />
          <DetailRow label="WhatsApp" value={row.whatsapp} />
          <DetailRow label="Gender" value={GENDER_LABEL[row.gender] ?? row.gender} />
          <DetailRow label="Age" value={AGE_LABEL[row.ageBracket] ?? row.ageBracket} />
          <DetailRow
            label="Location"
            value={`${GOVERNORATE_LABEL[row.governorate] ?? row.governorate} · ${row.city}`}
          />
          <DetailRow
            label="Current status"
            value={STATUS_LABEL[row.currentStatus] ?? row.currentStatus}
          />
          <DetailRow
            label="Skills"
            value={(row.skills ?? [])
              .map((s) => SKILL_LABEL[s] ?? s)
              .join(" · ")}
          />
          {row.linkedinUrl && (
            <DetailRow
              label="LinkedIn"
              value={
                <a
                  href={row.linkedinUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-green hover:underline"
                >
                  {row.linkedinUrl}
                </a>
              }
            />
          )}
          {(row.founders ?? []).length > 0 && (
            <DetailRow
              label="Co-founders"
              value={
                <ul className="space-y-1">
                  {row.founders.map((f, i) => (
                    <li key={i}>
                      {f.name}
                      {f.linkedinUrl && (
                        <>
                          {" "}
                          —{" "}
                          <a
                            href={f.linkedinUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-brand-green hover:underline"
                          >
                            LinkedIn
                          </a>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              }
            />
          )}
        </section>

        <section className="space-y-3">
          <h3 className="text-xs font-mono uppercase tracking-widest text-brand-orange">
            Startup
          </h3>
          <DetailRow label="One-liner" value={row.startupDescription} wrap />
          <DetailRow label="City" value={row.startupCity} />
          <DetailRow
            label="Sector"
            value={umbrellaOf(row.sector)}
          />
          <DetailRow label="Stage" value={STAGE_LABEL[row.stage] ?? row.stage} />
          <DetailRow
            label="Established"
            value={row.establishYear != null ? String(row.establishYear) : "—"}
          />
          <DetailRow label="Problem solved" value={row.problemSolved} wrap />
          <DetailRow
            label="Has users / revenue"
            value={row.hasUsersOrRevenue ? "Yes" : "No"}
          />
          {row.hasUsersOrRevenue && (
            <DetailRow
              label="Annual revenue"
              value={
                row.annualRevenueRange
                  ? (REVENUE_RANGE_LABEL[row.annualRevenueRange] ??
                    row.annualRevenueRange)
                  : "—"
              }
            />
          )}
          {row.hasUsersOrRevenue && (
            <DetailRow
              label="Customer count"
              value={
                row.customerCount != null
                  ? row.customerCount.toLocaleString("en-US")
                  : "—"
              }
            />
          )}
          {row.differentiator && (
            <DetailRow
              label="Differentiator (legacy)"
              value={row.differentiator}
              wrap
            />
          )}
          {row.targetAudience && (
            <DetailRow
              label="Target audience (legacy)"
              value={row.targetAudience}
              wrap
            />
          )}
          {row.motivation && (
            <DetailRow
              label="Motivation (legacy)"
              value={row.motivation}
              wrap
            />
          )}
        </section>

        <p className="text-xs text-brand-muted">
          Submitted{" "}
          {new Date(row.createdAt).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </p>
      </div>
    </div>
  );
}

/**
 * Admin review editor: pipeline status select, free-text notes, and
 * a tag list. Lives at the top of the drawer so the committee can
 * triage without scrolling through the whole application first.
 */
function ReviewEditor({ row }: { row: NpdRegistration }) {
  const [status, setStatus] = useState<NpdAdminStatus>(row.adminStatus);
  const [notes, setNotes] = useState<string>(row.adminNotes ?? "");
  const [tags, setTags] = useState<string[]>(row.adminTags ?? []);
  const [tagDraft, setTagDraft] = useState<string>("");

  // Reset local state whenever the drawer rebinds to a different row,
  // or the row's data is refreshed via query invalidation.
  useEffect(() => {
    setStatus(row.adminStatus);
    setNotes(row.adminNotes ?? "");
    setTags(row.adminTags ?? []);
    setTagDraft("");
  }, [row.id, row.updatedAt, row.adminStatus, row.adminNotes, row.adminTags]);

  const review = useReviewNpdRegistration();

  const addTag = () => {
    const t = tagDraft.trim();
    if (!t) return;
    if (tags.includes(t)) {
      setTagDraft("");
      return;
    }
    if (tags.length >= 20) return;
    setTags([...tags, t]);
    setTagDraft("");
  };

  const removeTag = (t: string) => {
    setTags(tags.filter((x) => x !== t));
  };

  const dirty =
    status !== row.adminStatus ||
    (notes.trim() || null) !== (row.adminNotes ?? null) ||
    JSON.stringify(tags) !== JSON.stringify(row.adminTags ?? []);

  const save = () => {
    review.mutate({
      id: row.id,
      patch: {
        adminStatus: status,
        adminNotes: notes.trim() ? notes.trim() : null,
        adminTags: tags,
      },
    });
  };

  return (
    <section className="rounded-xl border border-brand-border bg-brand-navy/60 p-4 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs font-mono uppercase tracking-widest text-brand-orange">
          Review
        </h3>
        <AdminStatusPill status={row.adminStatus} />
      </div>

      {/* Status select */}
      <div className="space-y-1.5">
        <label className="block text-[11px] font-semibold uppercase tracking-widest text-brand-muted">
          Pipeline status
        </label>
        <div className="flex flex-wrap gap-2">
          {ADMIN_STATUS_ORDER.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                status === s
                  ? ADMIN_STATUS_CLASS[s]
                  : "border-brand-border text-brand-muted hover:text-white"
              }`}
            >
              {ADMIN_STATUS_LABEL[s]}
            </button>
          ))}
        </div>
      </div>

      {/* Tags */}
      <div className="space-y-1.5">
        <label className="block text-[11px] font-semibold uppercase tracking-widest text-brand-muted">
          Tags
        </label>
        <div className="flex flex-wrap gap-1.5">
          {tags.length === 0 && (
            <span className="text-xs text-brand-muted">No tags yet.</span>
          )}
          {tags.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 rounded-full border border-brand-border bg-brand-navy px-2 py-0.5 text-xs text-white"
            >
              {t}
              <button
                type="button"
                onClick={() => removeTag(t)}
                className="text-brand-muted hover:text-red-400"
                title="Remove tag"
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2 pt-1">
          <input
            type="text"
            value={tagDraft}
            onChange={(e) => setTagDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addTag();
              }
            }}
            placeholder="Add a tag…"
            maxLength={60}
            className="flex-1 px-2.5 py-1.5 text-xs bg-brand-navy border border-brand-border rounded-md text-white placeholder-brand-muted focus:outline-none focus:border-brand-green"
          />
          <button
            type="button"
            onClick={addTag}
            disabled={!tagDraft.trim()}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-brand-muted border border-brand-border rounded-md hover:text-white disabled:opacity-40"
          >
            <Plus size={12} /> Add
          </button>
        </div>
      </div>

      {/* Notes */}
      <div className="space-y-1.5">
        <label className="block text-[11px] font-semibold uppercase tracking-widest text-brand-muted">
          Notes
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          maxLength={5000}
          placeholder="Internal review notes — not shown to the founder."
          className="w-full px-3 py-2 text-sm bg-brand-navy border border-brand-border rounded-md text-white placeholder-brand-muted focus:outline-none focus:border-brand-green"
        />
      </div>

      <div className="flex items-center justify-end gap-2">
        {review.isSuccess && !dirty && (
          <span className="text-[11px] text-brand-green">Saved.</span>
        )}
        {review.isError && (
          <span className="text-[11px] text-red-400">Save failed.</span>
        )}
        <button
          type="button"
          onClick={save}
          disabled={!dirty || review.isPending}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-brand-green rounded-md hover:bg-brand-green/90 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Save size={12} />
          {review.isPending ? "Saving…" : "Save review"}
        </button>
      </div>
    </section>
  );
}

function DetailRow({
  label,
  value,
  wrap,
}: {
  label: string;
  value: React.ReactNode;
  wrap?: boolean;
}) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 text-sm">
      <span className="text-brand-muted">{label}</span>
      <span
        className={`text-white ${wrap ? "whitespace-pre-wrap leading-relaxed" : ""}`}
      >
        {value || <span className="text-brand-muted">—</span>}
      </span>
    </div>
  );
}
